/**
 * The flattened shape of an Apex player lookup, shared by everywhere that
 * verifies an in-game ID (profile editor, tournament join prompt).
 *
 * The upstream /api/apex-stats payload is deep and loosely typed; this reduces
 * it to the handful of fields the UI actually renders.
 */

export const APEX_GAME_NAME = 'apex-legends';

/** Platform codes the Apex stats API expects. */
export type ApexPlatformCode = 'PC' | 'PS4' | 'X1';

export const APEX_PLATFORM_OPTIONS: readonly {
  value: ApexPlatformCode;
  label: string;
}[] = [
  { value: 'PC', label: 'PC' },
  { value: 'PS4', label: 'PS' },
  { value: 'X1', label: 'Xbox' },
] as const;

/** Apex platform code -> the platform we store on user_games. */
export const APEX_CODE_TO_PLATFORM: Record<
  ApexPlatformCode,
  'PC' | 'PS5' | 'Xbox'
> = {
  PC: 'PC',
  PS4: 'PS5',
  X1: 'Xbox',
};

export interface ApexCandidate {
  level: number;
  levelPrestige: number;
  accountLevel: number;
  rankName?: string;
  rankDiv?: number;
  rankScore?: number;
  platform: string;
  rankImg?: string;
  levelImg: string;
  selectedLegend?: string;
  legendIcon?: string;
}

export const toApexCandidate = (player: unknown): ApexCandidate | null => {
  if (!player || typeof player !== 'object') return null;
  const data = player as Record<string, unknown>;
  const global = data.global as Record<string, unknown> | undefined;
  if (!global || typeof global.level !== 'number') return null;

  const legends = data.legends as Record<string, unknown> | undefined;
  const selected = legends?.selected as Record<string, unknown> | undefined;
  const gameInfo = selected?.gameInfo as Record<string, unknown> | undefined;
  const badges = gameInfo?.badges as Array<{ name?: string; value?: number }> | undefined;
  const accountLevelBadge = badges?.find((b) => b?.name === 'Account Level');
  const levelPrestige = (global.levelPrestige as number) || 0;
  const accountLevel =
    typeof accountLevelBadge?.value === 'number'
      ? accountLevelBadge.value
      : (global.level as number) + levelPrestige * 500;

  const rank = global.rank as Record<string, unknown> | undefined;
  const imgAssets = selected?.ImgAssets as Record<string, unknown> | undefined;

  return {
    level: global.level as number,
    levelPrestige,
    accountLevel,
    rankName: rank?.rankName as string | undefined,
    rankDiv: rank?.rankDiv as number | undefined,
    rankScore: rank?.rankScore as number | undefined,
    platform: (global.platform as string) || '',
    rankImg: rank?.rankImg as string | undefined,
    levelImg: `https://apexlegendsstatus.com/core/level_badge/?level=${accountLevel}`,
    selectedLegend: selected?.LegendName as string | undefined,
    legendIcon: imgAssets?.icon as string | undefined,
  };
};

/** Look up one player. Returns the candidate, or a reason it failed. */
export async function verifyApexPlayer(
  player: string,
  platform: ApexPlatformCode,
  signal?: AbortSignal
): Promise<{ candidate: ApexCandidate | null; error: string | null }> {
  try {
    const response = await fetch(
      `/api/apex-stats?player=${encodeURIComponent(player)}&platform=${encodeURIComponent(platform)}`,
      { signal }
    );
    if (!response.ok) {
      return {
        candidate: null,
        error: response.status === 404 ? 'Player not found' : 'Failed to verify',
      };
    }
    const candidate = toApexCandidate(await response.json());
    return candidate
      ? { candidate, error: null }
      : { candidate: null, error: 'Player not found' };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { candidate: null, error: null };
    }
    return { candidate: null, error: 'Failed to verify' };
  }
}
